import {
    Button,
    Select,
    type SelectOption,
    SimpleTooltip,
    TableSettingsDialog,
    TableToolbar,
} from '@rio-cloud/rio-uikit';
import { useNavigate, useLocation } from 'react-router';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { resolveAssignmentSelection } from '../constants';
import { getSortOptionId, getSortOptionsSupport } from './SortOptionsSupport';
import TaggedSearchSelect from '../../taggedSearchSelect';
import useDarkMode from '../../../hooks/useDarkMode';
import type { SupportViewType } from '../types';
import {
    DEFAULT_SUPPORT_TABLE_COLUMN_ORDER,
    DISABLED_SUPPORT_TABLE_COLUMNS,
    isDefaultSupportTableColumnsSettings,
    SUPPORT_TABLE_COLUMNS,
    type SupportTableColumn,
} from './supportTableColumns';

interface Props {
    handleSearch: (text: string) => void;
    handleSupportType: (subject: string) => void;
    handleDn: (text: string) => void;
    handleSort: (optionId: string) => void;
    optionsAssigned: SelectOption[];
    search?: string;
    dn?: string;
    sortBy?: string;
    sortDirection?: string;
    supportType?: string;
    handleAssignedChange: (values: string[]) => void;
    assignedSelection: string[];
    onOpenSettings: () => void;
    viewType: SupportViewType;
    onViewTypeChange: (viewType: SupportViewType) => void;
    columnOrder: SupportTableColumn[];
    hiddenColumns: SupportTableColumn[];
    onColumnChange: (columnOrder: string[], hiddenColumns: string[]) => void;
}

const ToollBar = ({
    handleSearch,
    handleSupportType,
    handleDn,
    optionsAssigned,
    handleSort,
    search,
    dn,
    sortBy,
    sortDirection,
    supportType,
    handleAssignedChange,
    assignedSelection,
    onOpenSettings,
    viewType,
    onViewTypeChange,
    columnOrder,
    hiddenColumns,
    onColumnChange,
}: Props) => {
    const intl = useIntl();
    const isDarkMode = useDarkMode();
    const navigate = useNavigate();
    const location = useLocation();
    const [isTableSettingsOpen, setIsTableSettingsOpen] = useState(false);

    const canRestoreColumns = !isDefaultSupportTableColumnsSettings(columnOrder, hiddenColumns);

    const restoreDefaultColumns = () => {
        if (!canRestoreColumns) {
            return;
        }
        onColumnChange([...DEFAULT_SUPPORT_TABLE_COLUMN_ORDER], []);
    };

    const handleChange = (value: string) => {
        handleSearch(value);
    };

    const handleChangeSupportType = (sub?: string) => {
        if (!sub) return;
        handleSupportType(sub);
    };

    const handleChangeDn = (value: string) => {
        handleDn(value);
    };

    const handleChangeAssigned = (selected: string[]) => {
        const nextSelection = resolveAssignmentSelection(selected, assignedSelection);
        handleAssignedChange(nextSelection);
    };

    const goToNewSupportCall = () => {
        navigate({
            pathname: location.pathname + '/new-support-call',
            search: location.search,
        });
    };

    const supportTypeOptions = useMemo(
        () => [
            {
                id: 'TECHNICAL_SUPPORT',
                label: intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.supportType.technical' }),
            },
            {
                id: 'VOLKSTOTAL',
                label: intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.supportType.volkstotal' }),
            },
            {
                id: 'all',
                label: intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.supportType.all' }),
            },
        ],
        [intl]
    );

    const sortOptionsSupport = useMemo(() => getSortOptionsSupport(intl), [intl]);
    const tableColumnLabels = useMemo(
        () =>
            Object.fromEntries(
                SUPPORT_TABLE_COLUMNS.map(column => [column.id, intl.formatMessage({ id: column.labelId })])
            ),
        [intl]
    );

    const handleChangeSort = (optionId?: string) => {
        if (!optionId || optionId === 'HEADER') return;
        handleSort(optionId);
    };

    return (
        <div className='margin-top-20'>
            <div>
                <span className='text-medium text-size-18' style={{ color: isDarkMode ? '#e5e5e5' : '#2A3740' }}>
                    {intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.title' })}
                </span>
            </div>

            <TableToolbar>
                <div className='table-toolbar-container'>
                    <div className='table-toolbar-group flex-1-1 align-items-end flex-nowrap'>
                        <div className='table-toolbar-column table-toolbar-column-spacer'>
                            <Button
                                onClick={goToNewSupportCall}
                                bsStyle={Button.PRIMARY}
                                iconName='rioglyph-plus'
                                style={{ width: 150 }}
                            >
                                {intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.newRequest' })}
                            </Button>
                        </div>
                        <div className='table-toolbar-column table-toolbar-column-spacer w-full'>
                            <SimpleTooltip
                                placement='top'
                                content={intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.searchPlaceholder' })}
                            >
                                <input
                                    type='text'
                                    className='form-control'
                                    placeholder={intl.formatMessage({
                                        id: 'intl-msg:tas.support.toolbar.searchPlaceholder',
                                    })}
                                    id='chassisSearch'
                                    value={search}
                                    onChange={event => handleChange(event.target.value)}
                                    maxLength={17}
                                    style={{ height: '34px', minHeight: '34px', maxHeight: '34px', lineHeight: '34px' }}
                                />
                            </SimpleTooltip>
                        </div>

                        <div className='table-toolbar-column table-toolbar-column-spacer w-full'>
                            <label htmlFor='supportType'>
                                {intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.supportType' })}
                            </label>
                            <Select
                                id='supportType'
                                placeholder={intl.formatMessage({
                                    id: 'intl-msg:tas.support.toolbar.selectPlaceholder',
                                })}
                                value={[supportType || '']}
                                options={supportTypeOptions}
                                useFilter
                                className='border'
                                noItemMessage={intl.formatMessage({
                                    id: 'intl-msg:tas.support.toolbar.noSupportTypeFound',
                                })}
                                onChange={selected => handleChangeSupportType(selected?.id)}
                            />
                        </div>

                        <div className='table-toolbar-column table-toolbar-column-spacer w-full'>
                            <label htmlFor='dn'>{intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.dn' })}</label>
                            <input
                                type='text'
                                className='form-control'
                                id='dn'
                                value={dn}
                                onChange={event => handleChangeDn(event.target.value)}
                                maxLength={17}
                                style={{ height: '34px', minHeight: '34px', maxHeight: '34px', lineHeight: '34px' }}
                            />
                        </div>
                        <div className='table-toolbar-column table-toolbar-column-spacer w-full min-width-250-xl max-width-250'>
                            <TaggedSearchSelect
                                id='assigned'
                                classNameContainerScroll='max-height-60'
                                label={intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.assignments' })}
                                placeholder={intl.formatMessage({
                                    id: 'intl-msg:tas.support.toolbar.assignment.searchPlaceholder',
                                })}
                                emptyMessage={intl.formatMessage({
                                    id: 'intl-msg:tas.support.toolbar.noAssignmentFound',
                                })}
                                options={optionsAssigned}
                                value={assignedSelection}
                                onChange={handleChangeAssigned}
                                allValue='all'
                            />
                        </div>
                        <div className='table-toolbar-column min-width-250-xl w-full'>
                            <label htmlFor='sortby'>
                                {intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.sortBy' })}
                            </label>
                            <Select
                                id='sortby'
                                placeholder={intl.formatMessage({
                                    id: 'intl-msg:tas.support.toolbar.selectPlaceholder',
                                })}
                                value={[getSortOptionId(sortBy || '', sortDirection || '')]}
                                options={sortOptionsSupport}
                                useFilter
                                className='border'
                                noItemMessage={intl.formatMessage({
                                    id: 'intl-msg:tas.support.toolbar.noSortOptionFound',
                                })}
                                onChange={selected => handleChangeSort(selected?.id)}
                                dropdownClassName='width-100pct'
                            />
                        </div>

                        <div className='table-toolbar-column'>
                            <div className='btn-toolbar'>
                                <Button iconOnly iconName='rioglyph rioglyph-filter' onClick={onOpenSettings} />
                            </div>
                        </div>

                        <div className='table-toolbar-column'>
                            <div className='btn-toolbar'>
                                <div className='btn-group'>
                                    <Button
                                        iconOnly
                                        iconName='rioglyph rioglyph-cards-table'
                                        className={viewType === 'kanban' ? 'active' : ''}
                                        onClick={() => onViewTypeChange('kanban')}
                                    />
                                    <Button
                                        iconOnly
                                        iconName='rioglyph rioglyph-th-list'
                                        className={viewType === 'list' ? 'active' : ''}
                                        onClick={() => onViewTypeChange('list')}
                                    />
                                </div>
                            </div>
                        </div>

                        {viewType === 'list' ? (
                            <div className='table-toolbar-column'>
                                <div className='btn-toolbar'>
                                    <Button
                                        id='move'
                                        iconName='rioglyph rioglyph-settings'
                                        iconOnly
                                        onClick={() => setIsTableSettingsOpen(true)}
                                    />
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            </TableToolbar>

            {viewType === 'list' && isTableSettingsOpen ? (
                <TableSettingsDialog
                    key={columnOrder.join('|')}
                    className='support-table-settings-dialog'
                    show={isTableSettingsOpen}
                    title={
                        <span className='display-flex justify-content-between align-items-center'>
                            <span>{intl.formatMessage({ id: 'intl-msg:tas.support.tableSettings.title' })}</span>
                            <Button
                                variant={Button.VARIANT_LINK}
                                iconName='rioglyph-revert'
                                onClick={restoreDefaultColumns}
                                disabled={!canRestoreColumns}
                            >
                                {intl.formatMessage({ id: 'intl-msg:tas.support.tableSettings.reset' })}
                            </Button>
                        </span>
                    }
                    onHide={() => setIsTableSettingsOpen(false)}
                    onApply={(newColumnOrder, newHiddenColumns) => {
                        onColumnChange(newColumnOrder, newHiddenColumns);
                        setIsTableSettingsOpen(false);
                    }}
                    onDiscard={() => setIsTableSettingsOpen(false)}
                    defaultColumnOrder={DEFAULT_SUPPORT_TABLE_COLUMN_ORDER}
                    defaultHiddenColumns={[]}
                    columnOrder={columnOrder}
                    hiddenColumns={hiddenColumns}
                    columnLabels={tableColumnLabels}
                    disabledColumns={DISABLED_SUPPORT_TABLE_COLUMNS}
                    closeButtonText={intl.formatMessage({ id: 'intl-msg:tas.support.tableSettings.close' })}
                    cancelButtonText={intl.formatMessage({ id: 'intl-msg:tas.support.tableSettings.cancel' })}
                    applyButtonText={intl.formatMessage({ id: 'intl-msg:tas.support.tableSettings.apply' })}
                    resetButtonText=''
                    searchPlaceholder={intl.formatMessage({
                        id: 'intl-msg:tas.support.tableSettings.searchPlaceholder',
                    })}
                    notFoundMessage={intl.formatMessage({ id: 'intl-msg:tas.support.tableSettings.notFound' })}
                    autoLabel={intl.formatMessage({ id: 'intl-msg:tas.support.tableSettings.auto' })}
                />
            ) : null}
        </div>
    );
};

export default ToollBar;
